// Calendar math in the profile's time zone without a date library. A "civil date"
// is a plain YYYY-MM-DD string, times of day are minutes since local midnight.

export interface ZonedTime {
  date: string;
  minutes: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
};

const partsOf = (instant: Date, timeZone: string) => {
  const values: Record<string, number> = {};
  formatterFor(timeZone)
    .formatToParts(instant)
    .forEach(part => {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    });
  return values;
};

const pad = (value: number): string => String(value).padStart(2, '0');

export const toZoned = (instant: Date | string, timeZone: string): ZonedTime => {
  const parts = partsOf(typeof instant === 'string' ? new Date(instant) : instant, timeZone);
  return {
    date: `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`,
    minutes: parts.hour * 60 + parts.minute,
  };
};

const offsetMs = (instantMs: number, timeZone: string): number => {
  const parts = partsOf(new Date(instantMs), timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asUtc - Math.floor(instantMs / 1000) * 1000;
};

// Instant of a local wall-clock time; across a DST change the offset is refined once.
export const fromZoned = (date: string, minutes: number, timeZone: string): Date => {
  const [year, month, day] = date.split('-').map(Number);
  const naive = Date.UTC(year, month - 1, day, 0, minutes);
  const guess = naive - offsetMs(naive, timeZone);
  return new Date(naive - offsetMs(guess, timeZone));
};

export const todayIn = (timeZone: string): string => toZoned(new Date(), timeZone).date;

export const addDays = (date: string, days: number): string => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
};

// Monday = 0, like the backend.
export const weekdayOf = (date: string): number => {
  const [year, month, day] = date.split('-').map(Number);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
};

export const startOfWeek = (date: string): string => addDays(date, -weekdayOf(date));

export const timeToMinutes = (value: string | null | undefined): number => {
  if (!value) return 0;
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

export const formatMinutes = (minutes: number): string =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

export const formatDate = (
  date: string,
  locale: string,
  options: Intl.DateTimeFormatOptions
): string =>
  new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`)
  );
